import type { ActionArgs, LoaderArgs } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { zfd } from "zod-form-data";
import { z } from "zod";
import { validateFormData } from "~/utils/validation";
import { createUrlSafeRandomString } from "~/utils/hash-utils.server";
import bcrypt from "bcryptjs";
import { prisma } from "~/db.server";
import { sendEmail } from "~/services/sengrid.server";
import { ENV } from "~/config/env.server";
import { getPublicUrl } from "~/utils";
import { Form, useActionData, useLoaderData, useNavigation } from "@remix-run/react";
import { AUDIT_EVENT } from "~/modules/audit/audit-events";

import { useTranslation } from "react-i18next";
import * as React from "react";
import { ButtonPrimary } from "~/ui/infra/Button";
import { FormControl } from "~/ui/infra/FormControl";
import { InputOutlined } from "~/ui/infra/Input";
import { getAuditRequestContext } from "~/modules/audit/audit-request.server";
import { recordAuditEventBackground } from "~/models/audit.server";
import {
  AUTH_THROTTLE_PURPOSE,
  getAuthThrottleStatus,
  registerAuthFailure,
} from "~/models/auth-failure-throttle.server";
import { isCognitoAuthEnabled } from "~/services/cognito.server";
import { requestCognitoPasswordResetLink } from "~/services/cognito-password-reset.server";

type ForgotPasswordFieldErrors = {
  email?: string[];
};

function getForgotPasswordActionMeta(actionData: unknown) {
  if (!actionData || typeof actionData !== "object") {
    return {
      intent: undefined as "reset-email-sent" | undefined,
      email: undefined as string | undefined,
      formError: undefined as string | undefined,
      fieldErrors: undefined as ForgotPasswordFieldErrors | undefined,
    };
  }

  const data = actionData as Record<string, unknown>;
  const fieldErrors =
    data.fieldErrors && typeof data.fieldErrors === "object"
      ? (data.fieldErrors as ForgotPasswordFieldErrors)
      : undefined;

  return {
    intent: data.intent === "reset-email-sent" ? ("reset-email-sent" as const) : undefined,
    email: typeof data.email === "string" ? data.email : undefined,
    formError:
      typeof data.formError === "string"
        ? data.formError
        : Array.isArray(data.formErrors) && typeof data.formErrors[0] === "string"
          ? data.formErrors[0]
          : undefined,
    fieldErrors,
  };
}

const forgotPasswordFormSchema = z.object({
  operation: z.literal("request-reset"),
  email: z.string().email(),
});

const schema = zfd.formData(forgotPasswordFormSchema);

function passwordResetThrottledFlatten() {
  return new z.ZodError([
    {
      code: "custom",
      path: ["email"],
      message: "Too many password reset attempts. Please try again later.",
    },
  ]).flatten();
}

export async function loader(_args: LoaderArgs) {
  return json({ cognitoEnabled: isCognitoAuthEnabled() });
}

export async function action({ request }: ActionArgs) {
  const requestContext = getAuditRequestContext(request, "/forgot-password");
  const formParseResult = await validateFormData(request, schema);

  if (!formParseResult.success) {
    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
      entity: { type: "resetPassword" },
      requestContext,
      status: "failure",
      httpStatus: 400,
      errorCode: "VALIDATION_ERROR",
    });
    return json(formParseResult.error.flatten(), { status: 400 });
  }

  const { email } = formParseResult.data;
  const normalizedEmail = email.trim().toLowerCase();

  if (isCognitoAuthEnabled()) {
    const throttle = await getAuthThrottleStatus(AUTH_THROTTLE_PURPOSE.PASSWORD_RESET, normalizedEmail);
    if (throttle.blocked && throttle.lockedUntil) {
      recordAuditEventBackground({
        eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
        entity: { type: "resetPassword" },
        requestContext,
        status: "failure",
        httpStatus: 429,
        errorCode: "PASSWORD_RESET_THROTTLED",
        after: { email: normalizedEmail },
      });
      return json({ ...passwordResetThrottledFlatten(), email: normalizedEmail }, { status: 429 });
    }

    await registerAuthFailure(AUTH_THROTTLE_PURPOSE.PASSWORD_RESET, normalizedEmail);

    const user = await prisma.user.findFirst({
      where: { email: { equals: normalizedEmail, mode: "insensitive" } },
      select: { id: true },
    });

    const publicUrl = getPublicUrl(request);
    const resetPageUrl = `http${ENV.NODE_ENV !== "development" ? "s" : ""}://${publicUrl}/reset-password-invitation`;

    const result = await requestCognitoPasswordResetLink({
      email: normalizedEmail,
      resetPageUrl,
    });
    if (!result.ok) {
      recordAuditEventBackground({
        eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
        entity: { type: "resetPassword", id: user?.id },
        requestContext,
        status: "failure",
        httpStatus: 500,
        errorCode: "COGNITO_RESET_EMAIL_FAILED",
        after: { email: normalizedEmail, provider: "cognito_reset_link" },
      });
    } else {
      recordAuditEventBackground({
        eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
        entity: { type: "resetPassword", id: user?.id },
        requestContext,
        status: "success",
        after: {
          email: normalizedEmail,
          provider: "cognito_reset_link",
          emailSent: result.emailSent,
        },
      });
    }

    return json({
      ok: true,
      intent: "reset-email-sent" as const,
      email: normalizedEmail,
    });
  }

  const user = await prisma.user.findUnique({ where: { email } });

  if (!user) {
    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
      entity: { type: "resetPassword" },
      requestContext,
      status: "failure",
      httpStatus: 400,
      errorCode: "USER_NOT_FOUND",
      after: { email },
    });
    return json(
      new z.ZodError([
        {
          code: "custom",
          path: ["email"],
          message: "",
          params: {
            i18n: "",
          },
        },
      ]).flatten(),
      { status: 400 }
    );
  }

  await prisma.resetPassword.deleteMany({
    where: {
      userId: user.id,
    },
  });

  const token = createUrlSafeRandomString();

  const hashedToken = await bcrypt.hash(token, 10);

  await prisma.resetPassword.create({
    data: {
      userId: user.id,
      hashedToken,
    },
  });

  const publicUrl = getPublicUrl(request);

  try {
    await sendResetPassword(email, token, user.id, publicUrl);
    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
      entity: { type: "resetPassword", id: user.id },
      requestContext,
      status: "success",
      after: { email },
    });
    return redirect("/login");
  } catch (error) {
    await prisma.resetPassword.deleteMany({
      where: {
        userId: user.id,
      },
    });

    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_PASSWORD_RESET_REQUEST,
      entity: { type: "resetPassword", id: user.id },
      requestContext,
      status: "failure",
      httpStatus: 500,
      errorCode: "EMAIL_SEND_FAILED",
      after: { email },
    });
    return json(
      new z.ZodError([
        {
          code: "custom",
          path: ["email"],
          message: "Failed to send reset password email. Please try again later or contact support.",
          params: {
            i18n: "errors.email_send_failed",
          },
        },
      ]).flatten(),
      { status: 500 }
    );
  }
}

export default function ForgotPassword() {
  const actionData = useActionData<typeof action>();
  const { intent, email, formError, fieldErrors } = getForgotPasswordActionMeta(actionData);
  const errors = fieldErrors;
  const { cognitoEnabled } = useLoaderData<typeof loader>();
  const { t } = useTranslation();
  const [requestEmail, setRequestEmail] = React.useState("");
  const navigation = useNavigation();
  const submittingOperation =
    navigation.state !== "idle" && navigation.formMethod?.toLowerCase() === "post"
      ? String(navigation.formData?.get("operation") ?? "")
      : "";
  const isSubmittingRequestReset = submittingOperation === "request-reset";
  const requestedEmail = email ?? "";
  const canSendResetRequest = z.string().email().safeParse(requestEmail).success;
  const resetEmailSent = cognitoEnabled && intent === "reset-email-sent";

  React.useEffect(() => {
    if (requestedEmail) {
      setRequestEmail(requestedEmail);
    }
  }, [requestedEmail]);

  return (
    <div className="py-10 text-light">
      <div className="mx-auto max-w-xs">
        <h1 className="text-center text-2xl">{t("app.forgot_password.password_recovery")}</h1>
        {resetEmailSent ? (
          <div className="mt-6 space-y-4 text-center text-sm text-secondary-25">
            <p>
              If an account exists for <strong className="text-light">{requestedEmail}</strong>, we sent a password
              reset link to that email address.
            </p>
            <p>The link expires in one hour. Open it to choose a new password.</p>
          </div>
        ) : (
          <Form method="post" className="mt-6 flex flex-col gap-y-4">
            <input type="hidden" name="operation" value="request-reset" />
            <FormControl>
              <FormControl.Label>{t("common.email")}</FormControl.Label>
              <InputOutlined
                name="email"
                placeholder={t("common.registration_email") as string}
                value={requestEmail}
                onChange={(event) => setRequestEmail(event.currentTarget.value.trim())}
              />
              <FormControl.Error>{errors?.email ?? formError}</FormControl.Error>
            </FormControl>
            <p className="text-center text-sm text-secondary-25 sm:px-4">
              Enter your account email to receive a password reset link.
            </p>
            <ButtonPrimary
              className="mt-2 w-full"
              disabled={!canSendResetRequest || isSubmittingRequestReset}
              isLoading={isSubmittingRequestReset}
            >
              {cognitoEnabled ? "Send reset link" : t("common.send_code")}
            </ButtonPrimary>
          </Form>
        )}
      </div>
    </div>
  );
}

async function sendResetPassword(email: string, token: string, userId: string, domain: string) {
  const template = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>Your Email Title</title>
  </head>
  <body>
    <h1>Reset password</h1>
    <a href="http${
      ENV.NODE_ENV !== "development" ? "s" : ""
    }://${domain}/reset-password-invitation?t=${token}&u=${userId}">Click here to reset the password</a>
    <p>Best regards,</p>
    <p>Reconizer team</p>
  </body>
  </html>
  `;

  await sendEmail(email, "Reset Password", template, { disableClickTracking: true });
}
